import { Check, ArrowRight, Sparkles, Crown, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, SectionHead } from "../components/ui-bits";
import { useApp } from "../context/AppContext";

export default function Pricing() {
  const { config, openBooking } = useApp();
  const plans = config.customer_plans || [];
  const planOrder = { "35day": 0, "15day": 1, daily: 2 };
  const ordered = [...plans].sort((a, b) => (planOrder[a.id] ?? 9) - (planOrder[b.id] ?? 9));

  return (
    <section id="pricing" className="light-section relative py-20 lg:py-28" data-testid="pricing-section">
      <div className="dot-grid-light pointer-events-none absolute inset-0" />
      <div className="container-x relative">
        <SectionHead
          eyebrow="Customer Plans"
          light
          title={<>Simple, Transparent Plans.</>}
          sub="One price. Doorstep service. No hidden charges — pick the care rhythm that fits your car."
          align="center"
        />

        <div className="mt-14 grid grid-cols-1 items-stretch gap-6 md:grid-cols-3">
          {ordered.map((p, i) => {
            const isPopular = p.badge === "POPULAR";
            const isBest = p.badge === "BEST VALUE";
            const perWash = p.washes > 1 ? Math.round(p.price / p.washes) : null;
            const dailyPlan = plans.find((x) => x.washes === 1);
            const totalIfDaily = perWash && dailyPlan ? dailyPlan.price * p.washes : null;
            const saveAmt = totalIfDaily ? totalIfDaily - p.price : 0;
            const savePct = totalIfDaily ? Math.round((saveAmt / totalIfDaily) * 100) : 0;
            return (
              <Reveal key={p.id} delay={i * 0.08} className="h-full">
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className={`relative flex h-full flex-col rounded-[1.75rem] p-7 ${
                    isBest
                      ? "border border-carza/40 bg-gradient-to-b from-[#f3efff] to-white shadow-[0_30px_70px_-30px_rgba(109,61,245,0.4)]"
                      : "card-light"
                  }`}
                  data-testid={`plan-card-${p.id}`}
                >
                  {(isPopular || isBest) && (
                    <span
                      className={`absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full px-4 py-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-white ${
                        isBest ? "bg-gradient-to-r from-carza to-carza-lite" : "bg-nighttext"
                      }`}
                      data-testid={`plan-badge-${p.id}`}
                    >
                      {isBest ? <Crown size={11} /> : <Sparkles size={11} />} {p.badge}
                    </span>
                  )}

                  <h3 className="font-display text-xl font-bold text-nighttext">{p.name}</h3>
                  <p className="mt-1 text-sm text-[#5c5470]">{p.tagline}</p>

                  <div className="mt-6 flex items-end gap-2">
                    <span className="font-display text-5xl font-bold tracking-tight text-nighttext" data-testid={`plan-price-${p.id}`}>
                      ₹{p.price.toLocaleString("en-IN")}
                    </span>
                    {perWash && <span className="pb-1.5 text-sm text-mist-2">₹{perWash}/wash</span>}
                    <span className="mb-1 ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-600/25 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700" data-testid={`plan-time-${p.id}`}>
                      <Clock size={10} /> ~30 min
                    </span>
                  </div>
                  {totalIfDaily && (
                    <p className="mt-2 text-[12px] font-medium text-emerald-600" data-testid={`plan-savings-${p.id}`}>
                      As one-time washes: ₹{totalIfDaily.toLocaleString("en-IN")} — you save ₹{saveAmt.toLocaleString("en-IN")} ({savePct}%)
                    </p>
                  )}

                  <ul className="mt-6 flex flex-1 flex-col gap-3 border-t border-black/[0.06] pt-6">
                    {p.includes.map((inc) => (
                      <li key={inc} className="flex items-start gap-2.5 text-sm text-[#2a2438]">
                        <span className="mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-carza-dim text-carza" style={{ width: 18, height: 18 }}>
                          <Check size={11} />
                        </span>
                        {inc}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => openBooking(p.id)}
                    className={`btn mt-7 w-full py-3.5 ${isBest ? "btn-primary" : "btn-outline-dark"}`}
                    data-testid={`plan-choose-${p.id}`}
                  >
                    Choose Plan <ArrowRight size={15} />
                  </button>
                  {p.id === "daily" && (
                    <p className="mt-3 text-center text-[12px] font-medium text-carza" data-testid="plan-try-note-daily">
                      New to CARZA? Try our services before you subscribe.
                    </p>
                  )}
                </motion.div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <p className="mx-auto mt-10 max-w-lg text-center text-xs leading-relaxed text-mist-2">
            Plan availability, inclusions and prices are being finalized and may vary by city and vehicle type. Final details are confirmed at booking.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
